import React, {useEffect, useState, lazy, Suspense} from "react";
import Header from "../components/header/Header";
import Greeting from "./greeting/Greeting";
import SplashScreen from "./splashScreen/SplashScreen";
import {splashScreen} from "../portfolio";
import {StyleProvider} from "../contexts/StyleContext";
import {useLocalStorage} from "../hooks/useLocalStorage";
import {applySeo, HOMEPAGE_SEO} from "../utils/seoHead";
import "./Main.scss";
import "./StartupProjects/StartupProjects.scss";

const Skills = lazy(() => import("./skills/Skills"));
const StackProgress = lazy(() => import("./skillProgress/skillProgress"));
const StartupProject = lazy(() => import("./StartupProjects/StartupProject"));
const ROICalculator = lazy(() => import("./roiCalculator/ROICalculator"));
const Education = lazy(() => import("./education/Education"));
const WorkExperience = lazy(() => import("./workExperience/WorkExperience"));
const Projects = lazy(() => import("./projects/Projects"));
const ProcessDiagram = lazy(() => import("./processDiagram/ProcessDiagram"));
const Achievement = lazy(() => import("./achievement/Achievement"));
const Profile = lazy(() => import("./profile/Profile"));
const ScrollToTopButton = lazy(() => import("./topbutton/Top"));

function SectionFallback({minHeight = 120}) {
  return <div aria-hidden="true" style={{minHeight}} />;
}

const Main = () => {
  const darkQuery =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
  const [isDark, setIsDark] = useLocalStorage(
    "isDark",
    !!(darkQuery && darkQuery.matches)
  );
  const [isShowingSplashAnimation, setIsShowingSplashAnimation] =
    useState(true);

  useEffect(() => {
    applySeo(HOMEPAGE_SEO);
  }, []);

  useEffect(() => {
    if (splashScreen.enabled) {
      const splashTimer = setTimeout(
        () => setIsShowingSplashAnimation(false),
        splashScreen.duration
      );
      return () => {
        clearTimeout(splashTimer);
      };
    }
  }, []);

  const changeTheme = () => {
    setIsDark(!isDark);
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark-mode", isDark);
    document.body.classList.toggle("dark-mode", isDark);

    return () => {
      document.documentElement.classList.remove("dark-mode");
      document.body.classList.remove("dark-mode");
    };
  }, [isDark]);

  return (
    <main className={isDark ? "portfolio-root dark-mode" : "portfolio-root"} id="main-content">
      <StyleProvider value={{isDark: isDark, changeTheme: changeTheme}}>
        {isShowingSplashAnimation && splashScreen.enabled ? (
          <SplashScreen />
        ) : (
          <>
            <Header />
            <Greeting />
            <Suspense fallback={<SectionFallback minHeight={240} />}>
              <WorkExperience />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={240} />}>
              <Skills />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={320} />}>
              <StackProgress />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={480} />}>
              <StartupProject />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={280} />}>
              <ROICalculator />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={240} />}>
              <Education />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={240} />}>
              <Projects />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={200} />}>
              <ProcessDiagram />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={240} />}>
              <Achievement />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={240} />}>
              <Profile />
            </Suspense>
            <Suspense fallback={null}>
              <ScrollToTopButton />
            </Suspense>
          </>
        )}
      </StyleProvider>
    </main>
  );
};

export default Main;
